import { useId, type ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { MODAL_TITLE_TYPE, TYPE } from '@/lib/typography';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';

export type ModalTone = 'neutral' | 'danger' | 'warning';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  open?: boolean;
  description?: ReactNode;
  footer?: ReactNode;
  tone?: ModalTone;
  showIcon?: boolean;
  role?: 'dialog' | 'alertdialog';
  wide?: boolean;
}

function toneIcon(tone: ModalTone) {
  switch (tone) {
    case 'danger':
      return { Icon: AlertTriangle, wrap: 'bg-ds-coral-tint text-ds-coral-ink' };
    case 'warning':
      return { Icon: AlertTriangle, wrap: 'bg-ds-amber-tint text-ds-amber-ink' };
    case 'neutral':
      return { Icon: Info, wrap: 'bg-ds-teal-tint text-ds-teal' };
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

export function Modal({
  title,
  onClose,
  children,
  open = true,
  description,
  footer,
  tone = 'neutral',
  showIcon = false,
  role = 'dialog',
  wide = false,
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const isDesktop = useIsDesktopNav();
  const { Icon, wrap } = toneIcon(tone);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-ds-scrim" />
      <div
        className={cn(
          'fixed inset-0 z-50 flex p-s5',
          isDesktop ? 'items-center justify-center' : 'items-end p-0',
        )}
      >
        <DialogPanel
          role={role}
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          className={cn(
            'w-full bg-ds-card p-s5 shadow-[var(--sh-md)]',
            wide ? 'max-w-slide-over' : 'max-w-modal',
            isDesktop ? 'rounded-card' : 'rounded-t-card px-s4 pb-[calc(16px+env(safe-area-inset-bottom))] pt-s3',
          )}
        >
          {!isDesktop ? <div className="mx-auto mb-s3 h-1 w-10 rounded-full bg-ds-line" aria-hidden /> : null}
          <div className="flex items-start gap-s4">
            {showIcon ? (
              <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', wrap)}>
                <Icon className="h-5 w-5" aria-hidden />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-s3">
                <DialogTitle id={titleId} className={cn(MODAL_TITLE_TYPE, 'flex-1')}>
                  {title}
                </DialogTitle>
                <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
                  <X />
                </Button>
              </div>
              {description ? (
                <p id={descId} className={cn(TYPE.body, 'mt-s2 text-ds-text-2')}>
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-s4">{children}</div>
          {footer ? (
            <div className="mt-s5 flex flex-col-reverse justify-end gap-s2 nav:flex-row">{footer}</div>
          ) : null}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
